import {
  Bell,
  Search,
  Users,
  Calendar,
  Heart,
  HandHeart,
  Award,
  MessageSquare,
  Home,
  User,
  Settings,
  Hash,
  FileText,
  Network,
  Shield,
  Clock,
  ThumbsUp,
  Briefcase,
  Building2,
  CheckCircle,
  ArrowLeft,
  LogOut,
  Lock,
  Eye,
  Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import civiCircleLogo from "../assets/civicircle-logo.jpg";
import kiwiPlaceholder from "../assets/donation-1.jpg"; // Placeholder for feed
import avatarPlaceholder from "../assets/donation-2.jpg"; // Placeholder for avatars
import {
  EventService,
  DashboardService,
  DropdownService,
} from "../services/APIs";
import type { Event } from "../utils/types/event";
import ZulipServices from "../services/APIs/zulipServices";
import MemberProfileService from "../services/APIs/MemberProfileService";
import SOPService, { type SOPResponse } from "../services/APIs/SOPService";
import FeedService from "../services/APIs/feedService";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import { getTenantId } from "../lib/storeManager";
import { get } from "../services/axiosUitils";

type FeedScope = "local" | "regional";
type PostProvenance = "internal" | "peer" | "official";
type PostType = "update" | "sop" | "resource" | "announcement";

// Placeholder components for missing ones
const GlobalSearch = ({ triggerClassName }: { triggerClassName?: string }) => {
  return (
    <div className={`relative ${triggerClassName ?? ""}`}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder="Search posts, events, members..."
        className="border-border bg-muted/50 pl-10"
      />
    </div>
  );
};

const MainNavigation = () => {
  const navigate = useNavigate();
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card lg:hidden">
      <div className="grid grid-cols-3 gap-1 p-2">
        <Button
          variant="ghost"
          size="sm"
          className="flex h-auto flex-col gap-1 py-2"
          onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
        >
          <Home className="h-5 w-5 text-primary" />
          <span className="text-[10px] font-medium text-primary">Home</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex h-auto flex-col gap-1 py-2"
          onClick={() => navigate(CitycollabNavigationRoutes.MESSAGES)}
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-[10px]">Messages</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex h-auto flex-col gap-1 py-2"
          onClick={() => navigate(CitycollabNavigationRoutes.PROFILE)}
        >
          <User className="h-5 w-5" />
          <span className="text-[10px]">Profile</span>
        </Button>
      </div>
    </nav>
  );
};

const ThreadSunsetIndicator = ({
  status,
  daysRemaining,
  closedDate,
}: {
  status?: string;
  daysRemaining?: number;
  closedDate?: string;
}) => {
  if (status === "closed") {
    return (
      <Badge variant="outline" className="text-[10px]">
        Closed {closedDate}
      </Badge>
    );
  }
  if (daysRemaining !== undefined && daysRemaining > 0) {
    return (
      <Badge variant="outline" className="text-[10px]">
        {daysRemaining} days left
      </Badge>
    );
  }
  return null;
};

const getThreadSunsetStatus = (
  hasLinkedThread: boolean,
  threadDaysRemaining?: number,
  threadClosed?: boolean
) => {
  if (threadClosed) {
    return "closed";
  }
  if (hasLinkedThread && threadDaysRemaining !== undefined) {
    if (threadDaysRemaining <= 0) {
      return "closed";
    }
    if (threadDaysRemaining <= 7) {
      return "warning";
    }
    return "active";
  }
  return "none";
};

const Index = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const memberId = localStorage.getItem("memberId");
    const userId = localStorage.getItem("userId");

    if (!accessToken) {
      navigate("/");
      return;
    }

    if (!memberId || memberId === userId) {
      navigate(CitycollabNavigationRoutes.MemberRegistrationPage);
    }
  }, [navigate]);
  const [viewMode, setViewMode] = useState<FeedScope>("local"); // "My City" is default
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState<boolean>(false);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);
  const [channelList, setChannelList] = useState<any[]>([]);
  const [openCommentPostId, setOpenCommentPostId] = useState<string | null>(
    null
  );
  const [commentTexts, setCommentTexts] = useState<{
    [postId: string]: string;
  }>({});
  const [expandedComments, setExpandedComments] = useState<{
    [postId: string]: boolean;
  }>({});
  const [likedPosts, setLikedPosts] = useState<Set<string>>(() => {
    // Initialize from localStorage on first load
    try {
      const stored = localStorage.getItem("likedPosts");
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      }
    } catch (error) {
      console.error("Error loading liked posts from localStorage:", error);
    }
    return new Set();
  });
  const [likingPostId, setLikingPostId] = useState<string | null>(null);

  // Save likedPosts to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "likedPosts",
        JSON.stringify(Array.from(likedPosts))
      );
    } catch (error) {
      console.error("Error saving liked posts to localStorage:", error);
    }
  }, [likedPosts]);

  // Handle logout - clear localStorage and navigate to auth page
  const handleLogout = () => {
    // Clear all specified localStorage items
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("zulipToken");
    localStorage.removeItem("zulipUserId");
    localStorage.removeItem("userId");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("memberId");

    // Navigate to auth page (or landing page / dashboard)
    navigate("/");
  };

  // Helper to get initials from name
  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0)?.toUpperCase() ?? "";
    const last = lastName?.charAt(0)?.toUpperCase() ?? "";
    return first + last || "U";
  };

  // Helper to resolve an image URL from a path or full URL
  const resolveImageUrl = useCallback((path?: string | null): string | null => {
    if (!path || typeof path !== "string") {
      return null;
    }

    // If it's already a full URL, return it
    try {
      new URL(path);
      return path;
    } catch {
      // Relative path handling
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

      // Ensure baseUrl does not end with / and path does not start with / to avoid double slashes,
      // OR handle both cases consistently
      const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
      const cleanPath = path.startsWith("/") ? path : `/${path}`;

      return `${cleanBase}${cleanPath}`;
    }
  }, []);

  // Helper to build profile avatar URL
  const getProfileAvatarUrl = (profile?: any): string | null => {
    const profileToUse = profile || memberProfile;
    const profileImage = profileToUse?.profilephoto || profileToUse?.profile_photo || profileToUse?.profile_image || null;
    return resolveImageUrl(profileImage);
  };

  // Fetch member profile
  const { data: memberProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["memberProfile"],
    queryFn: async () => {
      try {
        // Check if all required items exist in localStorage before calling API
        const memberId = localStorage.getItem("memberId");
        const accessToken = localStorage.getItem("accessToken");
        const tenantId =
          localStorage.getItem("tenantId") ??
          localStorage.getItem("tenant") ??
          getTenantId();

        if (!memberId) {
          console.warn(
            "Member ID not found in localStorage, skipping profile fetch"
          );
          return null;
        }

        if (!accessToken) {
          console.warn(
            "Access token not found in localStorage, skipping profile fetch"
          );
          return null;
        }

        if (!tenantId) {
          console.warn(
            "Tenant ID not found in localStorage, skipping profile fetch"
          );
          return null;
        }

        // Add a small delay to ensure member record is ready (same as MemberRegistrationPage behavior)
        await new Promise(resolve => setTimeout(resolve, 100));

        return await MemberProfileService.getMemberProfile();
      } catch (error) {
        console.error("Error fetching member profile:", error);
        console.error("Error details:", {
          message: (error as any)?.message,
          response: (error as any)?.response?.data,
          status: (error as any)?.response?.status,
        });
        // Don't throw error, just return null to prevent breaking the UI
        return null;
      }
    },
    enabled:
      !!localStorage.getItem("memberId") &&
      !!localStorage.getItem("accessToken") &&
      !!getTenantId(),
    refetchInterval: 60000, // Refetch every minute
    refetchOnWindowFocus: true,
    staleTime: 30000,
    retry: 2, // Retry twice on failure
    retryDelay: 1000, // Wait 1 second before retry
  });

  // Helper to format date like "Nov 25, 2025"
  const formatEventDate = (dateString: string) => {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        setUpcomingLoading(true);
        setUpcomingError(null);

        const organizationId = localStorage.getItem("OrganizationId");

        const data = await EventService.getAllEvents(false, {
          organization_id: organizationId ?? undefined,
          event: "upcoming",
        });

        setUpcomingEvents(data || []);
      } catch (error) {
        console.error("Error fetching upcoming events:", error);
        setUpcomingEvents([]);
        setUpcomingError("Unable to load upcoming events.");
      } finally {
        setUpcomingLoading(false);
      }
    };

    fetchUpcoming();
  }, []);

  // Fetch channels list
  const { data: channelsData, isLoading: isLoadingChannels } = useQuery({
    queryKey: ["zulip", "streams"],
    queryFn: async () => {
      const res = await ZulipServices.getChannelList();
      return res?.streams ?? [];
    },
  });

  useEffect(() => {
    if (channelsData?.length) {
      setChannelList(channelsData);
    } else {
      setChannelList([]);
    }
  }, [channelsData]);

  // Get organization ID from localStorage
  const organizationId = localStorage.getItem("OrganizationId");

  // Fetch recommended experts
  const { data: recommendedExpertsData, isLoading: isLoadingExperts } =
    useQuery({
      queryKey: ["recommended-experts"],
      queryFn: async () => {
        try {
          const data = await DashboardService.getRecommendedExperts(false);
          console.log("Recommended experts data:", data);
          return data;
        } catch (error) {
          console.error("Error fetching recommended experts:", error);
          return [];
        }
      },
      refetchOnWindowFocus: false,
      staleTime: 300000, // 5 minutes
    });

  // Fetch skills options to map skill IDs to names
  const { data: skillsOptions } = useQuery({
    queryKey: ["skill_interest"],
    queryFn: () => DropdownService.getDropdownData("skill_interest", false),
  });

  // Fetch SOPs for Quick Access
  const {
    data: sopsData,
    isLoading: isLoadingSOPs,
    error: sopsError,
  } = useQuery({
    queryKey: ["sops", organizationId],
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }
      try {
        return await SOPService.getAllSOPs(organizationId);
      } catch (error) {
        console.error("Error fetching SOPs:", error);
        return [];
      }
    },
    enabled: !!organizationId,
    refetchOnWindowFocus: false,
  });

  // Helper to format date for SOP display
  const formatSOPDate = (dateString: string) => {
    if (!dateString) {
      return "Recently";
    }
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Today";
    }
    if (diffDays === 1) {
      return "yesterday";
    }
    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} weeks ago`;
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Helper to format date to relative time (e.g., "2 hours ago")
  const formatRelativeTime = (dateString: string) => {
    if (!dateString) {
      return "Recently";
    }
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffSeconds = Math.floor(diffTime / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return "just now";
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
    }
    if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    }
    if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    }
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? "month" : "months"} ago`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  };

  // Fetch feeds - with query parameter for "My City" mode
  const {
    data: feedsData,
    isLoading: isLoadingFeeds,
    error: feedsError,
  } = useQuery({
    queryKey: ["feeds", viewMode],
    queryFn: async () => {
      try {
        // For "My City" (local), pass my_city: true
        // For "My Region" (regional), call API without parameters
        const params = viewMode === "local" ? { my_city: true } : undefined;
        console.log(`Feed Query - Mode: ${viewMode}, params:`, params);

        return await FeedService.getFeeds(params);
      } catch (error) {
        console.error("Error fetching feeds:", error);
        return [];
      }
    },
    refetchInterval: 60000, // Refetch every minute
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider stale to force refetch on mode switch
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["in-app-notifications", "unread-count"],
    queryFn: async () => {
      const response = await get<{ unread_count?: number }>(
        "/in-app-notifications/unread-count/",
        { notifyMessage: false }
      );
      return Number(response?.unread_count ?? 0);
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  // Mutation for posting comments
  const postCommentMutation = useMutation({
    mutationFn: async (payload: { feed: string; comment: string }) => {
      return await FeedService.createFeedComment(payload);
    },
    onSuccess: () => {
      // Invalidate and refetch feeds to show the new comment
      queryClient.invalidateQueries({ queryKey: ["feeds"] });
    },
    onError: (error: any) => {
      console.error("Error posting comment:", error);
    },
  });

  // Mutation for liking/unliking posts
  const likePostMutation = useMutation({
    mutationFn: async (payload: { feed: string; reaction_type: number }) => {
      return await FeedService.addFeedReaction(payload);
    },
    onMutate: async _variables => {
      // Optimistically update the UI immediately
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(_variables.feed)) {
          newSet.delete(_variables.feed);
        } else {
          newSet.add(_variables.feed);
        }
        return newSet;
      });
    },
    onSuccess: () => {
      // Clear liking state
      setLikingPostId(null);
      // Invalidate and refetch feeds to update like count (but don't reset liked state)
      queryClient.invalidateQueries({ queryKey: ["feeds"] });
    },
    onError: (error: any, variables) => {
      console.error("Error liking post:", error);
      // Revert the optimistic update on error
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (newSet.has(variables.feed)) {
          newSet.delete(variables.feed);
        } else {
          newSet.add(variables.feed);
        }
        return newSet;
      });
      // Clear liking state on error
      setLikingPostId(null);
    },
  });

  const exploreCards = [
    {
      title: "Events",
      description: "Discover and join organization events and activities",
      icon: Calendar,
      href: CitycollabNavigationRoutes.EVENTS,
    },
    {
      title: "Directory",
      description: "Find and connect with members in your organization",
      icon: Users,
      href: CitycollabNavigationRoutes.DIRECTORY,
    },
    {
      title: "SOP Library",
      description: "Access standard operating procedures and documentation",
      icon: FileText,
      href: CitycollabNavigationRoutes.SOP_LIBRARY,
    },
    {
      title: "Channels",
      description: "Join channels and participate in group discussions",
      icon: Hash,
      href: CitycollabNavigationRoutes.CHANNELS,
    },
    {
      title: "Donations",
      description:
        "Support local causes and make a difference in your organization",
      icon: Heart,
      href: CitycollabNavigationRoutes.DONATIONS,
    },
    {
      title: "Volunteering",
      description: "Find volunteer opportunities and give back to your city",
      icon: HandHeart,
      href: CitycollabNavigationRoutes.VOLUNTEERING,
    },
    {
      title: "Sponsorship",
      description: "Partner with us and help grow our organization initiatives",
      icon: Award,
      href: CitycollabNavigationRoutes.SPONSORSHIP,
    },
    {
      title: "Messages",
      description: "Send messages and communicate with other members",
      icon: MessageSquare,
      href: CitycollabNavigationRoutes.MESSAGES,
    },
  ];

  const adminFeatures = [
    {
      title: "Retiree Marketplace",
      description: "Find expert consultants",
      icon: Briefcase,
      href: CitycollabNavigationRoutes.RETIREE_MARKETPLACE,
    },
  ];

  // Map feeds data to posts format with provenance support
  const posts = feedsData
    ? feedsData.map((feed: any) => {
      const authorName = feed.author_full_name ?? "User";
      const initials = getInitials(feed.author_first_name, "");
      // Handle attachments - could be null, string, or array
      let imageUrl = null;
      let attachmentUrl = null;
      if (feed.attachments) {
        let attachment = null;
        if (Array.isArray(feed.attachments) && feed.attachments.length > 0) {
          attachment = feed.attachments[0];
        } else if (typeof feed.attachments === "string") {
          attachment = feed.attachments;
        }

        if (attachment) {
          imageUrl = resolveImageUrl(attachment);
          attachmentUrl = imageUrl;
        }
      }

      // Determine provenance and post type from feed data
      const provenance: PostProvenance =
        feed.provenance === "official"
          ? "official"
          : feed.provenance === "peer"
            ? "peer"
            : "internal";
      const postType: PostType =
        feed.feed_type_name === "SOP"
          ? "sop"
          : feed.feed_type_name === "announcement"
            ? "announcement"
            : feed.feed_type_name === "resource"
              ? "resource"
              : "update";

      return {
        id: feed.id,
        author: authorName,
        role: feed.title ?? "",
        department: feed.visibility_label ?? "",
        city: feed.city ?? "San Francisco",
        time: formatRelativeTime(feed.created_on),
        content: feed.content ?? "",
        likes: feed.like_count ?? 0,
        comments: feed.comments_count ?? 0,
        shares: 0, // Not available in API response
        avatar: initials,
        image: imageUrl,
        attachment: attachmentUrl,
        feedComments: feed.comments ?? [],
        feedTypeName: feed.feed_type_name ?? "",
        provenance,
        postType,
        scope: feed.scope ?? ["local"], // Default to local if not provided
        commentsEnabled: feed.comments_enabled !== false,
        hasLinkedThread: feed.has_linked_thread ?? false,
        threadDaysRemaining: feed.thread_days_remaining,
        threadClosed: feed.thread_closed ?? false,
        threadClosedDate: feed.thread_closed_date,
        audience: feed.audience,
        policyDiscussionOpen: feed.policy_discussion_open ?? false,
        discussionCloses: feed.discussion_closes,
        userHasLiked: feed.user_has_liked ?? false, // Track if current user has liked this post
      };
    })
    : [];

  // Initialize and sync likedPosts state from feed data
  useEffect(() => {
    if (feedsData && Array.isArray(feedsData)) {
      console.log(
        "🔍 Feed Data for Like State:",
        feedsData.map(f => ({
          id: f.id,
          content: f.content?.substring(0, 30),
          user_has_liked: f.user_has_liked,
          like_count: f.like_count,
        }))
      );

      // Merge localStorage state with API data
      // localStorage state is already loaded in initial state
      setLikedPosts(prev => {
        const newSet = new Set(prev); // Start with localStorage data

        feedsData.forEach((feed: any) => {
          // If API provides user_has_liked, use that as source of truth
          if (feed.user_has_liked === true) {
            console.log("✅ API says post is liked:", feed.id);
            newSet.add(feed.id);
          } else if (feed.user_has_liked === false) {
            // If API explicitly says not liked, remove from set
            console.log("❌ API says post is NOT liked:", feed.id);
            newSet.delete(feed.id);
          }
          // If user_has_liked is undefined, keep localStorage state
        });

        console.log("📌 Final liked posts:", Array.from(newSet));
        return newSet;
      });
    }
  }, [feedsData]);

  // Map recommended experts from API to display format
  console.log("recommendedExpertsData:", recommendedExpertsData);
  console.log("Is array?", Array.isArray(recommendedExpertsData));
  console.log("skillsOptions:", skillsOptions);

  const experts =
    recommendedExpertsData &&
      Array.isArray(recommendedExpertsData) &&
      recommendedExpertsData.length > 0
      ? recommendedExpertsData.map((expert: any) => {
        console.log("Processing expert:", expert);
        // Get initials from full_name
        const getExpertInitials = (fullName: string) => {
          if (!fullName) {
            return "U";
          }
          const names = fullName.trim().split(" ");
          if (names.length >= 2) {
            return (
              names[0].charAt(0).toUpperCase() +
              names[names.length - 1].charAt(0).toUpperCase()
            );
          }
          return fullName.charAt(0).toUpperCase() || "U";
        };

        // Map skill IDs to skill names
        const skillNames =
          expert.skills && Array.isArray(expert.skills) && skillsOptions
            ? expert.skills
              .map((skillId: string) => {
                const skill = skillsOptions.find(
                  (opt: any) => opt.value === skillId || opt.id === skillId
                );
                return skill?.label ?? skill?.name ?? skillId;
              })
              .filter(Boolean)
            : [];

        const profilePhoto = getProfileAvatarUrl(expert);
        const mappedExpert = {
          id: expert.id,
          name:
            expert.full_name ??
            (`${expert.first_name ?? ""} ${expert.last_name ?? ""}`.trim() ||
              "Unknown"),
          city: expert.organization ?? "N/A",
          department: expert.department ?? "N/A",
          tags: skillNames,
          avatar: getExpertInitials(expert.full_name ?? ""),
          profile_photo: profilePhoto,
        };
        console.log("Mapped expert:", mappedExpert);
        return mappedExpert;
      })
      : [];

  console.log("Final experts array:", experts);

  const getProvenanceConfig = (provenance: PostProvenance) => {
    switch (provenance) {
      case "internal":
        return {
          label: "Internal – My City",
          className:
            "bg-provenance-internal text-foreground border-border/40",
          headerBg: "bg-muted/80",
        };
      case "peer":
        return {
          label: "Connected Peer – External City",
          className:
            "bg-provenance-peer text-blue-700 dark:text-blue-400 border-blue-500/30",
          headerBg: "bg-blue-50 dark:bg-blue-950/30",
        };
      case "official":
        return {
          label: "Official Announcement – Regulatory Authority",
          className:
            "bg-provenance-official text-amber-700 dark:text-amber-400 border-amber-500/30",
          headerBg: "bg-amber-50 dark:bg-amber-950/30",
        };
    }
  };

  const getModeHelperText = (mode: FeedScope) => {
    switch (mode) {
      case "local":
        return "Showing internal updates from your city";
      case "regional":
        return "Showing updates from your city and connected peer agencies";
    }
  };
  // Filter posts based on view mode
  // Note: For "My City" and "My Region", the API already filters the data,
  // so we just need to filter out regulatory posts for local/regional modes
  const filteredPosts = posts.filter(post => {
    if (viewMode === "local") {
      // My City: API already filters by my_city: true, just exclude regulatory
      return post.provenance !== "official";
    }
    if (viewMode === "regional") {
      // My Region: API returns all feeds, just exclude regulatory
      return post.provenance !== "official";
    }
    return true;
  });

  return (
    <div className="flex min-h-screen bg-background pb-20">
      {/* Main Content */}
      <div className="flex-1">
        {/* Top Header */}
        <header className="sticky top-0 z-40 border-b border-border bg-card">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-1 items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
                  className="shrink-0"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center gap-3 lg:hidden">
                  <img
                    src={civiCircleLogo}
                    alt="CiviCircle"
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  <div>
                    <h1 className="text-sm font-bold text-primary">
                      CiviCircle
                    </h1>
                    <p className="text-[10px] text-muted-foreground">
                      San Francisco
                    </p>
                  </div>
                </div>
                <div className="hidden flex-1 items-center gap-4 lg:flex">
                  <img
                    src={civiCircleLogo}
                    alt="CiviCircle"
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <GlobalSearch triggerClassName="flex-1" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(CitycollabNavigationRoutes.MESSAGES)}
                >
                  <MessageSquare className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  onClick={() =>
                    navigate(CitycollabNavigationRoutes.NOTIFICATIONS)
                  }
                  title="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(CitycollabNavigationRoutes.SETTINGS)}
                >
                  <Settings className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Mode Selector - Not a Filter */}
        <div className="border-b border-border bg-card">
          <div className="px-6 py-4">
            <div className="flex flex-col items-center gap-3">
              {/* Mode Toggle Buttons */}
              <div className="flex items-center gap-2 md:gap-3">
                <Button
                  variant={viewMode === "local" ? "default" : "outline"}
                  size="default"
                  onClick={() => setViewMode("local")}
                  className="min-w-[90px] md:min-w-[130px]"
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">My City</span>
                  <span className="sm:hidden">City</span>
                </Button>
                <div className="h-8 w-px bg-border" />
                <Button
                  variant={viewMode === "regional" ? "default" : "outline"}
                  size="default"
                  onClick={() => setViewMode("regional")}
                  className="min-w-[90px] md:min-w-[130px]"
                >
                  <Network className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">My Region</span>
                  <span className="sm:hidden">Region</span>
                </Button>
              </div>

              {/* Mode Helper Text */}
              <p className="text-xs text-muted-foreground text-center">
                {getModeHelperText(viewMode)}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="px-6 py-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Left Sidebar - Profile & Actions */}
            <div className="space-y-6 lg:col-span-3">
              {/* User Profile Card */}
              <Card>
                <CardContent className="p-6 text-center">
                  {isLoadingProfile ? (
                    <div className="py-8 text-center text-xs text-muted-foreground">
                      Loading profile...
                    </div>
                  ) : (
                    <>
                      {(() => {
                        // Defensively unwrap arrays or .data just in case
                        let profileData = memberProfile?.data || memberProfile;
                        if (Array.isArray(profileData)) {
                          profileData = profileData[0];
                        }
                        return (
                          <>
                            <div className="mx-auto mb-3 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-primary text-xl font-semibold text-primary-foreground">
                              {getProfileAvatarUrl(profileData) ? (
                                <img
                                  src={getProfileAvatarUrl(profileData) ?? ""}
                                  alt={
                                    profileData?.first_name
                                      ? `${profileData.first_name}${profileData.last_name ? ` ${profileData.last_name}` : ""}`
                                      : "Profile"
                                  }
                                  className="h-full w-full object-cover"
                                  onError={e => {
                                    (e.target as HTMLImageElement).src = avatarPlaceholder;
                                    (e.target as HTMLImageElement).onerror = null;
                                  }}
                                />
                              ) : (
                                getInitials(
                                  profileData?.first_name,
                                  profileData?.last_name
                                )
                              )}
                            </div>
                            <h3 className="mb-1 text-base font-bold text-foreground">
                              {profileData?.first_name
                                ? `${profileData.first_name}${profileData.last_name ? ` ${profileData.last_name}` : ""}`
                                : (profileData?.full_name || profileData?.name || "User")}
                            </h3>
                            <p className="mb-4 text-xs text-muted-foreground">
                              {profileData?.organization ?? "Member"}
                            </p>

                            {/* Stats */}
                            <div className="mb-4 grid grid-cols-2 gap-3">
                              <div
                                className="cursor-pointer rounded-lg bg-muted/50 p-3 transition-colors hover:bg-muted"
                                onClick={() => navigate(CitycollabNavigationRoutes.CHANNELS)}
                              >
                                <p className="mb-1 text-xs text-muted-foreground">
                                  Committees
                                </p>
                                <p className="text-lg font-bold text-foreground">
                                  {profileData?.committee_count ?? 0}
                                </p>
                              </div>
                              <div
                                className="cursor-pointer rounded-lg bg-muted/50 p-3 transition-colors hover:bg-muted"
                                onClick={() => navigate(CitycollabNavigationRoutes.SOP_LIBRARY)}
                              >
                                <p className="mb-1 text-xs text-muted-foreground">
                                  SOPs Owned
                                </p>
                                <p className="text-lg font-bold text-foreground">
                                  {profileData?.sop_count ??
                                    profileData?.sops_owned ??
                                    0}
                                </p>
                              </div>
                            </div>

                            <Button
                              variant="default"
                              className="mt-4 w-full"
                              onClick={() => {
                                const memberIdFromApi =
                                  profileData?.member_id ?? profileData?.id;
                                const memberIdFromStorage =
                                  localStorage.getItem("memberId");
                                const memberId =
                                  memberIdFromApi ?? memberIdFromStorage;

                                if (memberId) {
                                  navigate(
                                    `${CitycollabNavigationRoutes.PROFILE}?memberId=${memberId}`
                                  );
                                } else {
                                  navigate(CitycollabNavigationRoutes.PROFILE);
                                }
                              }}
                            >
                              Edit Profile
                            </Button>
                          </>
                        );
                      })()}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Quick Access */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Quick Access</CardTitle>
                  <CardDescription className="text-xs">
                    Access all member features and resources
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {exploreCards
                      .filter(card => card.title !== "Messages")
                      .map(card => (
                        <Button
                          key={card.title}
                          variant="outline"
                          className="w-full justify-start"
                          size="sm"
                          onClick={() => navigate(card.href)}
                        >
                          <card.icon className="mr-2 h-4 w-4" />
                          {card.title}
                        </Button>
                      ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recommended Experts */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Recommended Experts</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => navigate(CitycollabNavigationRoutes.DIRECTORY)}
                    >
                      View All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingExperts ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      Loading recommended experts...
                    </div>
                  ) : experts.length === 0 ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      No experts recommended at this time.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {experts.slice(0, 3).map(expert => (
                        <div
                          key={expert.id}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/profile/${expert.id}`)}
                        >
                          <Avatar className="h-9 w-9 shrink-0">
                            <AvatarImage
                              src={expert.profile_photo ?? undefined}
                              alt={expert.name}
                              onError={e => {
                                (e.target as HTMLImageElement).src = avatarPlaceholder;
                                (e.target as HTMLImageElement).onerror = null;
                              }}
                            />
                            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                              {expert.avatar}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-sm text-foreground truncate">
                              {expert.name}
                            </h4>
                            <p className="text-[10px] text-muted-foreground truncate mb-1">
                              {expert.department} • {expert.city}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {expert.tags.slice(0, 2).map((tag: string) => (
                                <Badge
                                  key={tag}
                                  variant="secondary"
                                  className="text-[9px] px-1 py-0 h-4"
                                >
                                  {tag}
                                </Badge>
                              ))}
                              {expert.tags.length > 2 && (
                                <span className="text-[9px] text-muted-foreground">
                                  +{expert.tags.length - 2}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>

            {/* Center Column - Operational Feed */}
            <div className="space-y-6 lg:col-span-6">
              {/* Operational Feed Header */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xl font-bold text-foreground">
                  Operational Feed
                </h2>
              </div>

              {/* Updates */}
              <div className="space-y-4">
                {isLoadingFeeds ? (
                  <Card>
                    <CardContent className="py-8 text-center text-sm text-muted-foreground">
                      Loading activity feed...
                    </CardContent>
                  </Card>
                ) : feedsError ? (
                  <Card>
                    <CardContent className="py-8 text-center text-sm text-destructive">
                      Failed to load activity feed. Please try again later.
                    </CardContent>
                  </Card>
                ) : filteredPosts.length === 0 ? (
                  <Card className="p-8 text-center">
                    <div className="text-muted-foreground">
                      <Shield className="mx-auto mb-3 h-12 w-12 opacity-50" />
                      <p className="text-sm font-medium">
                        No updates in this mode
                      </p>
                      <p className="mt-1 text-xs">
                        Switch modes to see different content
                      </p>
                    </div>
                  </Card>
                ) : (
                  filteredPosts.map(post => {
                    const provenanceConfig = getProvenanceConfig(
                      post.provenance
                    );
                    const isOfficial = post.provenance === "official";
                    const isSOP =
                      post.postType === "sop" || post.postType === "resource";
                    const threadStatus = getThreadSunsetStatus(
                      post.hasLinkedThread,
                      post.threadDaysRemaining,
                      post.threadClosed
                    );

                    return (
                      <Card
                        key={post.id}
                        className={`overflow-hidden ${isOfficial ? "border-amber-500/30" : ""}`}
                      >
                        {/* Provenance Header Strip - Dominant, High Contrast */}
                        <div
                          className={`flex items-center justify-between border-b px-4 py-2.5 text-sm font-semibold ${provenanceConfig.headerBg} ${provenanceConfig.className}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-bold">
                              {provenanceConfig.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ThreadSunsetIndicator
                              status={threadStatus}
                              daysRemaining={post.threadDaysRemaining}
                              closedDate={post.threadClosedDate}
                            />
                            <span className="font-medium text-foreground/80">
                              {post.role || post.city}
                            </span>
                          </div>
                        </div>

                        {/* Official Audience Targeting */}
                        {isOfficial && post.audience && (
                          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-100/50 px-4 py-2 text-xs dark:border-amber-800 dark:bg-amber-900/20">
                            <Eye className="h-3 w-3 text-amber-600" />
                            <span className="font-medium text-amber-700 dark:text-amber-400">
                              Audience: {post.audience}
                            </span>
                          </div>
                        )}

                        <CardHeader className="pb-3">
                          <div className="flex items-start gap-3">
                            <div
                              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${isOfficial ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" : "bg-primary text-primary-foreground"}`}
                            >
                              {post.avatar}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <h3 className="truncate text-sm font-semibold text-foreground">
                                      {post.author}
                                    </h3>
                                    {post.feedTypeName && (
                                      <Badge
                                        variant="outline"
                                        className="px-1.5 py-0 text-[10px]"
                                      >
                                        {post.feedTypeName}
                                      </Badge>
                                    )}
                                    {isOfficial && (
                                      <Badge className="bg-amber-500 text-[10px] text-white hover:bg-amber-600">
                                        Official
                                      </Badge>
                                    )}
                                    {isSOP && (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px]"
                                      >
                                        SOP
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="truncate text-xs text-muted-foreground">
                                    {post.role}
                                  </p>
                                  <div className="mt-1 flex items-center gap-2">
                                    <Badge
                                      variant="outline"
                                      className="px-1.5 py-0 text-[10px]"
                                    >
                                      {post.department}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      • {post.time}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4 pt-0">
                          <p
                            className={`text-sm leading-relaxed ${isOfficial ? "font-medium text-foreground" : "text-foreground"}`}
                          >
                            {post.content}
                          </p>

                          {post.feedTypeName === "SOP" && post.attachment ? (
                            <div className="relative rounded-lg border border-border bg-muted/50 p-4">
                              <a
                                href={post.attachment ?? "#"}
                                download
                                className="absolute right-4 top-4 cursor-pointer text-primary hover:text-primary/80"
                                onClick={e => {
                                  e.preventDefault();
                                  if (post.attachment) {
                                    const link = document.createElement("a");
                                    link.href = post.attachment;
                                    link.download = "";
                                    link.target = "_blank";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                              >
                                <Download className="h-5 w-5" />
                              </a>
                              <div className="mb-3 pr-8">
                                <p className="mb-1 text-sm font-semibold text-foreground">
                                  SOP Guideline Available
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Access the Standard Operating Procedure
                                  document for this guideline.
                                </p>
                              </div>
                              <a
                                href={post.attachment ?? "#"}
                                download
                                className="inline-flex cursor-pointer items-center text-sm font-medium text-primary hover:underline"
                                onClick={e => {
                                  e.preventDefault();
                                  if (post.attachment) {
                                    const link = document.createElement("a");
                                    link.href = post.attachment;
                                    link.download = "";
                                    link.target = "_blank";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }
                                }}
                              >
                                Click to Download
                              </a>
                            </div>
                          ) : post.image ? (
                            <img
                              src={post.image}
                              alt="Post content"
                              className="h-48 w-full rounded-lg object-cover"
                              onError={e => {
                                (e.target as HTMLImageElement).src = kiwiPlaceholder;
                                (e.target as HTMLImageElement).onerror = null; // Prevent infinite loops
                              }}
                            />
                          ) : null}

                          {/* Stats - Professional, No Social Patterns */}
                          <div className="flex items-center gap-4 border-t border-border pt-2 text-xs text-muted-foreground">
                            {!isOfficial && (
                              <span className="flex items-center gap-1">
                                <ThumbsUp className="h-3 w-3" />
                                {post.likes} marked helpful
                              </span>
                            )}
                            {isOfficial && (
                              <span className="flex items-center gap-1 text-amber-600">
                                <CheckCircle className="h-3 w-3" />
                                {post.likes} managers acknowledged
                              </span>
                            )}
                            {post.comments > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" />
                                  {post.comments} discussion threads
                                </span>
                              </>
                            )}
                          </div>

                          {/* Display Comments - Show at least 2, with "View more" if more exist */}
                          {post.feedComments &&
                            post.feedComments.length > 0 && (
                              <div className="space-y-3 border-t border-border pt-3">
                                {(() => {
                                  const apiComments = (
                                    post.feedComments ?? []
                                  ).map(
                                    (
                                      comment: {
                                        user?: string;
                                        user_first_name?: string | null;
                                        user_full_name?: string | null;
                                        user_profile_photo?: string | null;
                                        comment?: string;
                                        created_on?: string;
                                        created_at?: string;
                                      },
                                      index: number
                                    ) => {
                                      let profilePhoto = comment.user_profile_photo;
                                      if (profilePhoto && !profilePhoto.startsWith("http")) {
                                        const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";
                                        profilePhoto = `${baseUrl}${profilePhoto.startsWith("/") ? "" : "/"}${profilePhoto}`;
                                      }

                                      return {
                                        id: comment.user ?? `comment-${index}`,
                                        user_first_name:
                                          comment.user_full_name ??
                                          comment.user_first_name ??
                                          "User",
                                        user_profile_photo: profilePhoto,
                                        comment: comment.comment ?? "",
                                        created_on:
                                          comment.created_on ??
                                          comment.created_at ??
                                          null,
                                        time: comment.created_on
                                          ? formatRelativeTime(comment.created_on)
                                          : comment.created_at
                                            ? formatRelativeTime(
                                              comment.created_at
                                            )
                                            : "Recently",
                                      };
                                    }
                                  );

                                  const isExpanded =
                                    expandedComments[post.id] ?? false;
                                  const commentsToShow = isExpanded
                                    ? apiComments
                                    : apiComments.slice(0, 2);
                                  const hasMoreComments =
                                    apiComments.length > 2;

                                  return (
                                    <>
                                      {commentsToShow.map(
                                        (comment: {
                                          id: string;
                                          user_first_name: string;
                                          user_profile_photo: string | null;
                                          comment: string;
                                          time: string;
                                        }) => (
                                          <div key={comment.id} className="flex gap-3">
                                            <Avatar className="h-8 w-8 shrink-0">
                                              <AvatarImage
                                                src={comment.user_profile_photo ?? undefined}
                                                alt={comment.user_first_name}
                                                onError={e => {
                                                  (e.target as HTMLImageElement).src = avatarPlaceholder;
                                                  (e.target as HTMLImageElement).onerror = null;
                                                }}
                                              />
                                              <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">
                                                {getInitials(
                                                  comment.user_first_name,
                                                  ""
                                                )}
                                              </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 space-y-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-foreground">
                                                  {comment.user_first_name}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                  {comment.time}
                                                </span>
                                              </div>
                                              <p className="text-sm text-foreground">
                                                {comment.comment}
                                              </p>
                                            </div>
                                          </div>
                                        )
                                      )}
                                      {hasMoreComments && !isExpanded && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-xs text-muted-foreground hover:text-foreground"
                                          onClick={() => {
                                            setExpandedComments(prev => ({
                                              ...prev,
                                              [post.id]: true,
                                            }));
                                          }}
                                        >
                                          View more comments (
                                          {apiComments.length - 2} more)
                                        </Button>
                                      )}
                                      {hasMoreComments && isExpanded && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-xs text-muted-foreground hover:text-foreground"
                                          onClick={() => {
                                            setExpandedComments(prev => {
                                              const newExpanded = { ...prev };
                                              delete newExpanded[post.id];
                                              return newExpanded;
                                            });
                                          }}
                                        >
                                          View less
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}

                          {/* Action Buttons - Governance Focused */}
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 border-t border-border pt-2">
                            {/* Mark as Helpful - For SOPs and Resources only */}
                            {!isOfficial && (
                              <Button
                                variant="ghost"
                                size="default"
                                onClick={async () => {
                                  if (likingPostId === post.id) {
                                    return;
                                  }
                                  try {
                                    setLikingPostId(post.id);
                                    await likePostMutation.mutateAsync({
                                      feed: post.id,
                                      reaction_type: 1,
                                    });
                                  } catch (error) {
                                    console.error(
                                      "Failed to like post:",
                                      error
                                    );
                                  }
                                }}
                                disabled={likingPostId === post.id}
                                className="flex-1"
                              >
                                <ThumbsUp
                                  className={`mr-2 h-4 w-4 ${likedPosts.has(post.id) ? "fill-red-500 text-red-500" : ""}`}
                                />
                                <span className="hidden xs:inline">
                                  {likingPostId === post.id
                                    ? "Liking..."
                                    : "Mark as Helpful"}
                                </span>
                                <span className="xs:hidden">
                                  {likingPostId === post.id
                                    ? "Liking..."
                                    : "Helpful"}
                                </span>
                              </Button>
                            )}

                            {/* Acknowledge Button - For Official Announcements only */}
                            {isOfficial && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-9 flex-1 border-amber-500/50 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                Acknowledge âœ”
                              </Button>
                            )}

                            {/* Discussion Thread - Not inline replies */}
                            {post.commentsEnabled ? (
                              <Button
                                variant="ghost"
                                size="default"
                                className="flex-1"
                                onClick={() => {
                                  setOpenCommentPostId(
                                    openCommentPostId === post.id
                                      ? null
                                      : post.id
                                  );
                                }}
                              >
                                <MessageSquare className="mr-2 h-4 w-4" />
                                <span className="hidden xs:inline">Open Discussion Thread</span>
                                <span className="xs:hidden">Open Thread</span>
                              </Button>
                            ) : (
                              <div className="flex h-9 flex-1 items-center justify-center gap-2 text-xs text-muted-foreground">
                                <Lock className="h-3 w-3" />
                                Comments Disabled
                              </div>
                            )}

                            {/* Policy Discussion for Official with timer */}
                            {isOfficial && post.policyDiscussionOpen && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-9 text-xs"
                              >
                                🧠 Policy Discussion
                                <span className="ml-2 text-muted-foreground">
                                  ({post.discussionCloses})
                                </span>
                              </Button>
                            )}
                          </div>

                          {/* Comment Section - Input Only */}
                          {openCommentPostId === post.id && (
                            <div className="space-y-4 border-t border-border pt-4">
                              <div className="space-y-2">
                                <Textarea
                                  placeholder="Write a comment..."
                                  value={commentTexts[post.id] || ""}
                                  onChange={e => {
                                    setCommentTexts(prev => ({
                                      ...prev,
                                      [post.id]: e.target.value,
                                    }));
                                  }}
                                  className="min-h-[80px] resize-none"
                                />
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setOpenCommentPostId(null);
                                      setCommentTexts(prev => {
                                        const newTexts = { ...prev };
                                        delete newTexts[post.id];
                                        return newTexts;
                                      });
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      const commentText =
                                        commentTexts[post.id]?.trim();
                                      if (!commentText) {
                                        return;
                                      }

                                      try {
                                        await postCommentMutation.mutateAsync({
                                          feed: post.id,
                                          comment: commentText,
                                        });
                                        setCommentTexts(prev => {
                                          const newTexts = { ...prev };
                                          delete newTexts[post.id];
                                          return newTexts;
                                        });
                                        setOpenCommentPostId(null);
                                      } catch (error) {
                                        console.error(
                                          "Failed to post comment:",
                                          error
                                        );
                                      }
                                    }}
                                    disabled={
                                      !commentTexts[post.id]?.trim() ||
                                      postCommentMutation.isPending
                                    }
                                  >
                                    {postCommentMutation.isPending
                                      ? "Posting..."
                                      : "Post"}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Column - Sidebar Widgets */}
            {/* Right Column - Sidebar Widgets */}
            <div className="lg:col-span-3 space-y-6">
              {/* Upcoming Events */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Upcoming Events</CardTitle>
                      <CardDescription className="text-xs">
                        Regional & local sessions
                      </CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => navigate(CitycollabNavigationRoutes.EVENTS)}
                    >
                      View All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {upcomingLoading ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      Loading upcoming events...
                    </div>
                  ) : upcomingError ? (
                    <div className="py-4 text-center text-xs text-destructive">
                      {upcomingError}
                    </div>
                  ) : upcomingEvents.length === 0 ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      No upcoming events found.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {upcomingEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() =>
                            navigate(
                              `${CitycollabNavigationRoutes.EVENTS}/${event.id}`
                            )
                          }
                        >
                          <h4 className="font-medium text-sm text-foreground mb-1">
                            {event.event_name}
                          </h4>
                          <p className="text-xs text-muted-foreground mb-2">
                            {formatEventDate(event.start_date)}{" "}
                            {event.address && <>• {event.address}</>}{" "}
                            {typeof event.interested_count === "number" && (
                              <>• {event.interested_count} attending</>
                            )}
                          </p>
                          <div className="flex items-center justify-between">
                            <Badge variant="secondary" className="text-[10px]">
                              {event.event_type ?? "event"}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs p-0"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(
                                  `${CitycollabNavigationRoutes.EVENTS}/${event.id}`
                                );
                              }}
                            >
                              Register →
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* My Departments & Channels */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">My Departments & Channels</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingChannels ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      Loading channels...
                    </div>
                  ) : channelList.length === 0 ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      No channels found.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {channelList.slice(0, 6).map((channel: any) => {
                        const channelId = channel.stream_id ?? channel.id;
                        const channelName = channel.name ?? "Unknown Channel";
                        const subscriberCount = channel.subscriber_count ?? 0;

                        return (
                          <div
                            key={channelId}
                            className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() =>
                              navigate(
                                `${CitycollabNavigationRoutes.CHANNELS}/${channelId}?channelId=${channelId}`
                              )
                            }
                          >
                            <span className="text-sm text-foreground truncate">
                              #{channelName}
                            </span>
                            {subscriberCount > 0 && (
                              <Badge
                                variant="destructive"
                                className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px]"
                              >
                                {subscriberCount > 99 ? "99+" : subscriberCount}
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* SOP Quick Access */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">SOP Quick Access</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => navigate(CitycollabNavigationRoutes.SOP_LIBRARY)}
                    >
                      Search All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingSOPs ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      Loading SOPs...
                    </div>
                  ) : sopsError ? (
                    <div className="py-4 text-center text-xs text-destructive">
                      Failed to load SOPs
                    </div>
                  ) : !sopsData || sopsData.length === 0 ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      No SOPs found
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sopsData.slice(0, 4).map((sop: SOPResponse) => (
                        <div
                          key={sop.id}
                          className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate(CitycollabNavigationRoutes.SOP_LIBRARY)}
                        >
                          <h4 className="font-medium text-sm text-foreground mb-1">
                            {sop.title || "Untitled SOP"}
                          </h4>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{sop.category || "No Category"}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Updated {formatSOPDate(sop.updated_on)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </div>
        </main>
      </div >

      {/* Bottom Navigation */}
      < MainNavigation />
    </div >
  );
};

export default Index;
